import { useEffect, useState } from "react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import { TypeBadge, StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { lookupPlz } from "../lib/plz";
import PortalAccessPanel from "../components/PortalAccessPanel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Search, Pencil, History, Container, Trash2, FileUp, User, MapPin, Tag, CheckSquare, Square, MoreHorizontal, ShoppingCart, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import { API_BASE } from "../lib/api";
import ImportDialog from "../components/ImportDialog";
import FilterPills from "../components/FilterPills";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";

const empty = { name: "", type: "B2B", email: "", phone: "", street: "", city: "", zip: "", state: "", country: "DE", vat_id: "", notes: "", payment_terms_days: 14, customer_number: "", tax_number: "", addr1_extra: "", phone2: "", email2: "", secondary_address: null, contact_persons: [] };
const fmtEUR = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [palletBalances, setPalletBalances] = useState({}); // {customer_id: open_balance}
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceCustomer, setPriceCustomer] = useState(null);
  const [selected, setSelected] = useState({});
  const confirm = useConfirm();
  const navigate = useNavigate();

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const allOn = rows.length > 0 && rows.every((r) => selected[r.id]);
    const next = {};
    if (!allOn) rows.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    // Preflight: erste Runde ohne force → zeigt uns Blockierte
    let res;
    try {
      const { data } = await api.post("/customers/bulk-delete", { ids: selectedIds });
      res = data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Bulk-Löschen");
      return;
    }
    const blocked = res.blocked || [];
    const deletable = selectedIds.filter((id) => !blocked.some((b) => b.id === id));
    const blockedDetails = blocked.map((b) => ({
      left: b.name || b.id.slice(-8),
      right: b.reason || "blockiert",
    }));
    const ok = await confirm({
      title: `${deletable.length} Kunde(n) löschen?`,
      description: blocked.length
        ? `${blocked.length} Kunde(n) sind blockiert (offene Paletten oder aktive Bestellungen) und werden übersprungen. ${deletable.length} weitere können gelöscht werden.`
        : `${deletable.length} Kunde(n) werden endgültig gelöscht. Bestehende Belege bleiben erhalten, verlieren aber die Kundenzuordnung.`,
      confirmLabel: deletable.length > 0 ? `${deletable.length}× löschen` : "OK",
      cancelLabel: "Abbrechen",
      variant: "danger",
      details: blockedDetails,
    });
    if (!ok || deletable.length === 0) return;
    try {
      const { data } = await api.post("/customers/bulk-delete", { ids: deletable });
      toast.success(`${data.deleted}/${data.total} Kunde(n) gelöscht`);
      setSelected({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (q) params.q = q;
    if (type !== "all") params.type = type;
    if (pendingOnly) params.pending = true;
    const r = await api.get("/customers", { params });
    setRows(r.data);
    setTotal(Number(r.headers["x-total-count"]) || r.data.length);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, type, page, pendingOnly]);
  useEffect(() => { setPage(1); }, [q, type, pendingOnly]);
  const loadPendingCount = async () => {
    try {
      const r = await api.get("/customers", { params: { pending: true, page_size: 1 } });
      setPendingCount(Number(r.headers["x-total-count"]) || 0);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { loadPendingCount(); /* eslint-disable-next-line */ }, [rows]);
  useEffect(() => {
    api.get("/pallets/accounts?party_type=customer").then((r) => {
      const map = {};
      for (const a of r.data) map[a.party_id] = a.open_balance;
      setPalletBalances(map);
    }).catch(() => {});
  }, []);

  const openCreate = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ ...empty, ...c, email: c.email || "" });
    setEditing(c.id);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, email: form.email || null };
    try {
      if (editing) await api.patch(`/customers/${editing}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Kunde aktualisiert" : "Kunde angelegt");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const openHistory = async (c) => {
    try {
      const { data } = await api.get(`/customers/${c.id}/history`);
      setHistory(data);
      setHistoryOpen(true);
    } catch (_e) { toast.error("Historie konnte nicht geladen werden"); }
  };

  const approveRegistration = async (c) => {
    try {
      await api.post(`/customers/${c.id}/approve-registration`);
      toast.success(`„${c.name}" freigegeben – Portal-Zugang aktiviert`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Freigabe fehlgeschlagen"); }
  };

  const removeCustomer = async (c) => {    const ok = await confirm({
      title: "Kunde löschen?",
      description: `„${c.name}" wird endgültig gelöscht. Bestehende Lieferscheine/Rechnungen bleiben zwar erhalten, verlieren aber die Kundenzuordnung.`,
      confirmLabel: "Endgültig löschen",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(`Kunde „${c.name}" gelöscht`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  return (
    <div data-testid="page-customers">
      <PageHeader
        eyebrow="CRM"
        title="Kunden"
        description="B2B Retailer, Distributoren und B2C Endkunden – editierbar und mit voller Kaufhistorie."
        actions={
          <div className="flex items-center gap-2">
            <div className="relative w-64 hidden md:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <Input placeholder="Kunde suchen (Name/E-Mail/PLZ)…"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     data-testid="input-customer-search"
                     className="pl-8 rounded-full h-9" />
            </div>
            <Button variant="outline" data-testid="btn-import-customers"
                    onClick={() => setImportOpen(true)}
                    className="rounded-full">
              <FileUp size={14} className="mr-1" /> Importieren
            </Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-customer" onClick={openCreate}
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                <Plus size={16} className="mr-1" /> Neuer Kunde
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="grid grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input required data-testid="input-customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Kundennummer <span className="text-[10px] text-[var(--text-muted)] font-normal ml-1">(automatisch)</span></Label>
                  <Input value={form.customer_number || ""} data-testid="input-customer-number"
                         placeholder={editing ? "" : "wird beim Speichern vergeben"}
                         onChange={(e) => setForm({ ...form, customer_number: e.target.value })} /></div>
                <div>
                  <Label>Typ</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="select-customer-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="B2B">B2B</SelectItem>
                      <SelectItem value="B2C">B2C</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>USt-ID</Label><Input value={form.vat_id || ""} onChange={(e) => setForm({ ...form, vat_id: e.target.value })} /></div>
                <div>
                  <Label>Zahlungsziel</Label>
                  <Select value={String(form.payment_terms_days ?? 14)}
                          onValueChange={(v) => setForm({ ...form, payment_terms_days: Number(v) })}>
                    <SelectTrigger data-testid="select-customer-payment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Sofort</SelectItem>
                      <SelectItem value="14">14 Tage</SelectItem>
                      <SelectItem value="30">30 Tage</SelectItem>
                      <SelectItem value="60">60 Tage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Telefon</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="col-span-2"><Label>Straße</Label><Input value={form.street || ""} onChange={(e) => setForm({ ...form, street: e.target.value })} /></div>
                <div><Label>PLZ</Label><Input data-testid="input-customer-zip" value={form.zip || ""} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, zip: v })); if (/^\d{5}$/.test(v)) lookupPlz(v).then((r) => r && setForm((f) => ({ ...f, city: r.city, state: r.state, country: r.country || "DE" }))); }} /></div>
                <div><Label>Ort</Label><Input data-testid="input-customer-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><Label>Bundesland</Label><Input data-testid="input-customer-state" value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                <div><Label>Land</Label><Input value={form.country || "DE"} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
                <div className="col-span-2"><Label>Notizen</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

                {/* Extended fields collapsible */}
                <details className="col-span-2 rounded-xl border border-[var(--border)] bg-[var(--pop-cream)]/30 p-3">
                  <summary className="font-bold text-sm cursor-pointer inline-flex items-center gap-2">
                    <User size={14} /> Erweiterte Kontaktdaten (Ansprechpartner &amp; 2. Adresse)
                  </summary>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div><Label>Steuernummer</Label>
                      <Input value={form.tax_number || ""}
                             onChange={(e) => setForm({ ...form, tax_number: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Adresszusatz 1</Label>
                      <Input value={form.addr1_extra || ""}
                             onChange={(e) => setForm({ ...form, addr1_extra: e.target.value })} /></div>
                    <div><Label>Telefon 2</Label>
                      <Input value={form.phone2 || ""}
                             onChange={(e) => setForm({ ...form, phone2: e.target.value })} /></div>
                    <div><Label>E-Mail 2</Label>
                      <Input value={form.email2 || ""}
                             onChange={(e) => setForm({ ...form, email2: e.target.value })} /></div>
                    <div className="col-span-2 mt-2 pt-2 border-t">
                      <div className="text-xs font-bold mb-2 inline-flex items-center gap-1">
                        <MapPin size={12} /> Zweite Adresse (Lager/Filiale)
                      </div>
                    </div>
                    <div className="col-span-2"><Label className="text-xs">Straße 2</Label>
                      <Input value={form.secondary_address?.street || ""}
                             onChange={(e) => setForm({ ...form, secondary_address: { ...(form.secondary_address || {}), street: e.target.value } })} /></div>
                    <div><Label className="text-xs">PLZ 2</Label>
                      <Input value={form.secondary_address?.zip || ""}
                             onChange={(e) => setForm({ ...form, secondary_address: { ...(form.secondary_address || {}), zip: e.target.value } })} /></div>
                    <div><Label className="text-xs">Ort 2</Label>
                      <Input value={form.secondary_address?.city || ""}
                             onChange={(e) => setForm({ ...form, secondary_address: { ...(form.secondary_address || {}), city: e.target.value } })} /></div>
                    <div className="col-span-2 mt-2 pt-2 border-t">
                      <div className="text-xs font-bold mb-2 inline-flex items-center gap-1">
                        <User size={12} /> Ansprechpartner ({(form.contact_persons || []).length}/2)
                      </div>
                      {(form.contact_persons || []).map((cp, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2 mb-2 p-2 rounded-lg bg-white border">
                          <Input placeholder="Vorname" value={cp.first_name || ""}
                                 onChange={(e) => {
                                   const list = [...(form.contact_persons || [])];
                                   list[i] = { ...list[i], first_name: e.target.value };
                                   setForm({ ...form, contact_persons: list });
                                 }} />
                          <Input placeholder="Nachname" value={cp.last_name || ""}
                                 onChange={(e) => {
                                   const list = [...(form.contact_persons || [])];
                                   list[i] = { ...list[i], last_name: e.target.value };
                                   setForm({ ...form, contact_persons: list });
                                 }} />
                          <Input placeholder="E-Mail" value={cp.email || ""}
                                 onChange={(e) => {
                                   const list = [...(form.contact_persons || [])];
                                   list[i] = { ...list[i], email: e.target.value };
                                   setForm({ ...form, contact_persons: list });
                                 }} />
                          <Input placeholder="Telefon" value={cp.phone || ""}
                                 onChange={(e) => {
                                   const list = [...(form.contact_persons || [])];
                                   list[i] = { ...list[i], phone: e.target.value };
                                   setForm({ ...form, contact_persons: list });
                                 }} />
                          <button type="button" onClick={() => {
                            const list = [...(form.contact_persons || [])];
                            list.splice(i, 1);
                            setForm({ ...form, contact_persons: list });
                          }} className="col-span-2 text-xs text-red-600 hover:underline text-right">Entfernen</button>
                        </div>
                      ))}
                      {(form.contact_persons || []).length < 2 && (
                        <button type="button"
                                data-testid="btn-add-contact-person"
                                onClick={() => setForm({ ...form,
                                  contact_persons: [...(form.contact_persons || []), {}] })}
                                className="text-xs font-bold text-[var(--pop-pink)] hover:underline">
                          + Ansprechpartner hinzufügen
                        </button>
                      )}
                    </div>
                  </div>
                </details>
                {editing && <PortalAccessPanel customer={form} />}
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid="btn-save-customer"
                          className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                    Speichern
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        {selectedIds.length > 0 && (
          <div className="flex items-center justify-between gap-4 px-4 py-3 bg-black text-white"
               data-testid="customers-bulk-toolbar">
            <div className="text-sm"><span className="font-bold">{selectedIds.length}</span> ausgewählt</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm"
                      className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10"
                      onClick={() => setSelected({})}
                      data-testid="btn-clear-customer-selection">Abwählen</Button>
              <Button size="sm"
                      className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold"
                      onClick={bulkDelete}
                      data-testid="btn-bulk-delete-customers">
                <Trash2 size={14} className="mr-1" /> Ausgewählte löschen ({selectedIds.length})
              </Button>
            </div>
          </div>
        )}

        {/* Suche + Filter */}
        <div className="p-3 border-b border-[var(--border)] flex items-center gap-2 flex-wrap">
          <button onClick={toggleAll} data-testid="customers-select-all"
                  className="shrink-0 hover:opacity-70" title="Alle auf dieser Seite auswählen">
            {rows.length > 0 && rows.every((r) => selected[r.id])
              ? <CheckSquare size={18} className="text-[var(--pop-pink)]" />
              : <Square size={18} className="text-[var(--text-muted)]" />}
          </button>
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input placeholder="Suche Name, E-Mail, PLZ…" value={q}
                   data-testid="input-search-customers"
                   onChange={(e) => setQ(e.target.value)}
                   className="pl-8 rounded-xl h-9 text-sm" />
          </div>
          <FilterPills
            options={[
              ["all", "Alle", "#e5e5e5"],
              ["B2B", "B2B", "#93c5fd"],
              ["B2C", "B2C", "#fbcfe8"],
            ]}
            value={type}
            onChange={setType}
            testidPrefix="filter-customer-type-"
          />
          <button onClick={() => setPendingOnly((v) => !v)} data-testid="filter-pending-registrations"
                  className={`inline-flex items-center gap-1 rounded-full px-3 h-8 text-xs font-bold border transition-colors ${pendingOnly ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"}`}>
            <UserCheck size={13} /> Ausstehende Registrierungen{pendingCount ? ` (${pendingCount})` : ""}
          </button>
          <span className="text-xs text-[var(--text-muted)] ml-auto">{rows.length} / {total}</span>
        </div>

        {/* Karten-/Listenansicht */}
        <div className="p-3 space-y-2" data-testid="customers-list">
          {rows.map((r) => (
            <div key={r.id} data-testid={`customer-card-${r.id}`}
                 onClick={() => navigate(`/kunden/${r.id}`)}
                 className={`group rounded-xl border p-4 cursor-pointer transition-colors ${
                   selected[r.id] ? "border-[var(--pop-pink)] bg-[var(--surface-alt)]" : "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--pop-pink)]/50"
                 }`}>
              <div className="flex items-start gap-3">
                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => toggleOne(r.id)}
                          data-testid={`select-customer-${r.id}`}
                          className="hover:opacity-70">
                    {selected[r.id]
                      ? <CheckSquare size={18} className="text-[var(--pop-pink)]" />
                      : <Square size={18} className="text-[var(--text-muted)]" />}
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display font-bold text-base truncate min-w-0 flex items-center gap-2">
                      <span className="truncate">{r.name}</span>
                      {r.registration_status === "pending" && (
                        <span data-testid={`pending-badge-${r.id}`}
                              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold">
                          Ausstehend
                        </span>
                      )}
                    </div>
                    <TypeBadge type={r.type} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5 text-xs text-[var(--text-muted)] font-mono">
                    {r.customer_number && (
                      <span data-testid={`customer-number-${r.id}`}>{r.customer_number}</span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={11} /> {[r.zip, r.city].filter(Boolean).join(" ") || "—"}
                    </span>
                    <span>· {r.country}</span>
                    {r.email && <span className="truncate">· {r.email}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    {r.registration_status === "pending" && (
                      <button data-testid={`btn-approve-registration-${r.id}`} onClick={() => approveRegistration(r)}
                              className="inline-flex items-center gap-1 rounded-full bg-amber-500 text-white px-4 h-8 text-xs font-bold hover:bg-amber-600">
                        <UserCheck size={13} /> Registrierung freigeben
                      </button>
                    )}
                    <button data-testid={`btn-order-customer-${r.id}`} onClick={() => navigate(`/bestellungen?new=1&customer=${r.id}`)}
                            className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-pink)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                      <ShoppingCart size={13} /> Bestellung aufgeben
                    </button>
                    <button data-testid={`btn-edit-customer-${r.id}`} onClick={() => navigate(`/kunden/${r.id}`)}
                            className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-yellow)] text-black px-4 h-8 text-xs font-bold hover:bg-[var(--pop-yellow-dark)]">
                      <Pencil size={13} /> Details öffnen
                    </button>
                    {palletBalances[r.id] > 0 && (
                      <Link to={`/paletten?customer=${r.id}`}
                            data-testid={`customer-pallets-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-3 h-8 text-xs font-bold hover:bg-red-200">
                        <Container size={13} /> {palletBalances[r.id].toLocaleString("de-DE")} Paletten offen
                      </Link>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`customer-menu-${r.id}`} title="Weitere Aktionen"
                                className="ml-auto h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--pop-pink)]">
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem data-testid={`btn-open-customer-${r.id}`} onClick={() => navigate(`/kunden/${r.id}`)}>
                          <User size={14} className="mr-2" /> Details öffnen
                        </DropdownMenuItem>
                        {r.type === "B2B" && (
                          <DropdownMenuItem data-testid={`btn-prices-${r.id}`}
                                            onClick={() => { setPriceCustomer(r); setPriceOpen(true); }}>
                            <Tag size={14} className="mr-2" /> Kundenpreise
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem data-testid={`btn-history-${r.id}`} onClick={() => openHistory(r)}>
                          <History size={14} className="mr-2" /> Historie
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem data-testid={`btn-delete-customer-${r.id}`} onClick={() => removeCustomer(r)} className="text-red-600">
                          <Trash2 size={14} className="mr-2" /> Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="text-center py-12 text-sm text-[var(--text-muted)]" data-testid="customers-empty">
              Noch keine Kunden.
            </div>
          )}
        </div>
        <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} onPageSizeChange={(n)=>{setPage(1);setPageSize(n);}} storageKey="customers" testIdPrefix="customers-pager" />
      </div>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Kaufhistorie · {history?.customer?.name}</DialogTitle></DialogHeader>
          {history && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="rounded-xl border p-3">
                  <div className="overline">Belege</div>
                  <div className="font-display font-black text-2xl">{history.count}</div>
                </div>
                <div className="rounded-xl border p-3">
                  <div className="overline">Netto gesamt</div>
                  <div className="font-display font-black text-2xl">{fmtEUR(history.total_net)}</div>
                </div>
                <div className="rounded-xl border p-3">
                  <div className="overline">Brutto gesamt</div>
                  <div className="font-display font-black text-2xl">{fmtEUR(history.total_gross)}</div>
                </div>
              </div>

              <div className="max-h-[50vh] overflow-y-auto" data-testid="customer-history-list">
                <Table className="table-zebra">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="overline">LS-Nr.</TableHead>
                      <TableHead className="overline">Rechnung</TableHead>
                      <TableHead className="overline">Datum</TableHead>
                      <TableHead className="overline">Status</TableHead>
                      <TableHead className="overline">Zahlung</TableHead>
                      <TableHead className="overline text-right">Brutto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell className="font-mono text-xs">{it.number}</TableCell>
                        <TableCell className="font-mono text-xs">{it.invoice_number || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{(it.delivery_date || it.shipped_at || "").slice(0, 10) || "—"}</TableCell>
                        <TableCell><StatusBadge status={it.status} /></TableCell>
                        <TableCell>{it.payment_status ? <StatusBadge status={it.payment_status} /> : "—"}</TableCell>
                        <TableCell className="text-right font-mono">{fmtEUR(it.gross)}</TableCell>
                      </TableRow>
                    ))}
                    {history.items.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-[var(--text-muted)]">Noch keine Belege.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen}
        previewEndpoint="/customers/import/preview"
        commitEndpoint="/customers/import/commit"
        templateUrl={`${API_BASE}/customers/import/template.xlsx`}
        previewKey="rows" onDone={() => load()}
        renderPreviewHeader={() => (<>
          <th className="text-left p-2">Zeile</th>
          <th className="text-left p-2">Kd-Nr.</th>
          <th className="text-left p-2">Firma</th>
          <th className="text-left p-2">Ort</th>
          <th className="text-left p-2">Aktion</th>
        </>)}
        renderPreviewRow={(row, i) => {
          const b = row.action === "create" ? "bg-green-100 text-green-700"
                  : row.action === "update" ? "bg-blue-100 text-blue-700"
                  : "bg-red-100 text-red-700";
          return (
            <tr key={i} className="border-t border-[var(--border)]">
              <td className="p-2 font-mono text-[var(--text-muted)]">{row._row}</td>
              <td className="p-2 font-mono">{row.customer_number || "—"}</td>
              <td className="p-2 font-semibold">{row.name || "—"}</td>
              <td className="p-2 text-xs">{[row.zip, row.city].filter(Boolean).join(" ")}</td>
              <td className="p-2"><span className={`pop-pill text-[10px] font-bold ${b}`}>{row.action}</span></td>
            </tr>
          );
        }} />

      {/* Customer Prices Dialog */}
      {priceOpen && priceCustomer && (
        <CustomerPricesDialog customer={priceCustomer}
                                 onClose={() => { setPriceOpen(false); setPriceCustomer(null); }} />
      )}
    </div>
  );
}


function CustomerPricesDialog({ customer, onClose }) {
  const [prices, setPrices] = useState([]);
  const [items, setItems] = useState([]);
  const [bundles, setBundles] = useState([]); // [{code,label,units}]
  const [draft, setDraft] = useState({});     // item_id -> {code: value}
  const [reason, setReason] = useState("");
  const [addItemId, setAddItemId] = useState("");
  const confirm = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get(`/customers/${customer.id}/prices`);
      setPrices(data || []);
      const d = {};
      (data || []).forEach((p) => { d[p.item_id] = { ...(p.bundle_prices || {}) }; });
      setDraft(d);
    } catch (_e) { setPrices([]); }
  };
  useEffect(() => {
    load();
    api.get("/items?page_size=500").then((r) => setItems((r.data || []).filter((it) => it.type === "finished")));
    api.get("/settings/bundles").then((r) => {
      const obj = r.data || {};
      const arr = Object.entries(obj).map(([code, cfg]) => ({ code, label: (cfg && cfg.label) || code, units: (cfg && cfg.units) || 0 }));
      arr.sort((a, b) => (a.units || 0) - (b.units || 0));
      setBundles(arr);
    }).catch(() => setBundles([]));
    /* eslint-disable-next-line */
  }, [customer.id]);

  const itemName = (id) => items.find((it) => it.id === id)?.name || prices.find((p) => p.item_id === id)?.item_name || id;
  const itemSku = (id) => items.find((it) => it.id === id)?.sku || prices.find((p) => p.item_id === id)?.item_sku || "";
  const rowIds = Object.keys(draft);
  const setCell = (itemId, code, val) => setDraft((d) => ({ ...d, [itemId]: { ...(d[itemId] || {}), [code]: val } }));

  const addRow = () => {
    if (!addItemId || draft[addItemId]) return;
    setDraft((d) => ({ ...d, [addItemId]: {} }));
    setAddItemId("");
  };

  const saveRow = async (itemId) => {
    const raw = draft[itemId] || {};
    const bundle_prices = {};
    for (const b of bundles) {
      const v = raw[b.code];
      if (v !== "" && v != null && !Number.isNaN(Number(v))) bundle_prices[b.code] = Number(v);
    }
    try {
      await api.post(`/customers/${customer.id}/prices`, { item_id: itemId, bundle_prices, reason: reason || null });
      toast.success(`Preise für „${itemName(itemId)}" gespeichert`);
      setReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const removeRow = async (itemId) => {
    const ok = await confirm({
      title: "Kundenpreise entfernen?",
      description: `Alle Verpackungspreise für „${itemName(itemId)}" werden gelöscht. Künftig gilt der Standardpreis.`,
      confirmLabel: "Löschen", variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/customers/${customer.id}/prices/${itemId}`);
      setDraft((d) => { const n = { ...d }; delete n[itemId]; return n; });
      toast.success("Preise entfernt"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const available = items.filter((it) => !draft[it.id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-customer-prices">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            <Tag className="inline mr-2 -mt-1" size={20} /> Kundenpreise je Verpackung · {customer.name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[var(--text-muted)] -mt-2 mb-3">
          Individuelle Preise pro <b>Verpackungseinheit</b> (Preis für 1× Display / Palette). Werden bei Lieferscheinen für diesen Kunden automatisch als Zeilenpreis übernommen und <b>überschreiben den Standardpreis</b>. Änderungen werden protokolliert.
        </div>

        {/* Produkt hinzufügen */}
        <div className="flex items-end gap-2 mb-3">
          <div className="flex-1">
            <Label className="text-xs">Produkt hinzufügen</Label>
            <Select value={addItemId} onValueChange={setAddItemId}>
              <SelectTrigger data-testid="select-add-price-item"><SelectValue placeholder="Produkt auswählen…" /></SelectTrigger>
              <SelectContent>
                {available.map((it) => (<SelectItem key={it.id} value={it.id}>{it.name} · <span className="font-mono">{it.sku}</span></SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={addRow} data-testid="btn-add-price-row" disabled={!addItemId}
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            <Plus size={14} className="mr-1" /> Zeile
          </Button>
        </div>

        {/* Preis-Matrix */}
        <div className="overflow-x-auto">
          <Table className="table-zebra">
            <TableHeader>
              <TableRow>
                <TableHead className="overline min-w-[150px]">Produkt</TableHead>
                {bundles.map((b) => (
                  <TableHead key={b.code} className="overline text-right">
                    {b.label}
                    <div className="text-[9px] font-normal normal-case text-[var(--text-muted)]">{b.units} Stk</div>
                  </TableHead>
                ))}
                <TableHead className="overline text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rowIds.map((itemId) => (
                <TableRow key={itemId} data-testid={`price-row-${itemId}`}>
                  <TableCell className="font-semibold">
                    {itemName(itemId)}
                    <div className="font-mono text-[10px] text-[var(--text-muted)]">{itemSku(itemId)}</div>
                  </TableCell>
                  {bundles.map((b) => (
                    <TableCell key={b.code} className="text-right">
                      <Input type="number" step="0.01" min="0" placeholder="—"
                             data-testid={`price-input-${itemId}-${b.code}`}
                             value={draft[itemId]?.[b.code] ?? ""}
                             onChange={(e) => setCell(itemId, b.code, e.target.value)}
                             className="w-24 text-right ml-auto h-8" />
                    </TableCell>
                  ))}
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => saveRow(itemId)} data-testid={`btn-save-price-${itemId}`}
                            className="text-xs font-bold text-[var(--pop-pink)] hover:underline mr-3">Speichern</button>
                    <button onClick={() => removeRow(itemId)} data-testid={`btn-del-price-${itemId}`}
                            className="text-xs text-red-600 hover:underline">Löschen</button>
                  </TableCell>
                </TableRow>
              ))}
              {rowIds.length === 0 && (
                <TableRow><TableCell colSpan={bundles.length + 2} className="text-center py-8 text-[var(--text-muted)]">
                  Noch keine Kundenpreise. Produkt oben hinzufügen.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-3">
          <Label className="text-xs">Grund / Notiz (optional, für Audit)</Label>
          <Input value={reason} placeholder="z. B. Rahmenvertrag Q1, Aktionspreis…" data-testid="input-price-reason"
                 onChange={(e) => setReason(e.target.value)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
